import React from 'react';

interface EntityData {
  id: string;
  entity_type: 'person' | 'place' | 'organization' | 'date' | 'event' | 'product' | 'concept';
  entity_name: string;
  entity_value: string;
  confidence: number;
  context?: string;
  start_position?: number;
  end_position?: number;
  metadata?: string;
}

interface TopicData {
  id: string;
  topic_name: string;
  topic_category: string;
  relevance_score: number;
  confidence: number;
  keywords: string;
  summary?: string;
  context?: string;
}

interface RelationshipData {
  id: string;
  entity1_name: string;
  entity2_name: string;
  relationship_type: string;
  relationship_strength: number;
  context?: string;
  confidence: number;
}

interface EntityTopicDisplayProps {
  entities: EntityData[];
  topics: TopicData[];
  relationships: RelationshipData[];
  size?: 'sm' | 'md' | 'lg';
  showDetails?: boolean;
}

const EntityTopicDisplay: React.FC<EntityTopicDisplayProps> = ({ 
  entities, 
  topics, 
  relationships, 
  size = 'md',
  showDetails = false 
}) => {
  const getEntityColor = (entityType: string) => {
    const entityColors: Record<string, string> = {
      person: 'text-blue-600 bg-blue-50 border-blue-200',
      place: 'text-green-600 bg-green-50 border-green-200',
      organization: 'text-purple-600 bg-purple-50 border-purple-200',
      date: 'text-orange-600 bg-orange-50 border-orange-200',
      event: 'text-pink-600 bg-pink-50 border-pink-200',
      product: 'text-indigo-600 bg-indigo-50 border-indigo-200',
      concept: 'text-gray-600 bg-gray-50 border-gray-200',
    };
    return entityColors[entityType] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getTopicColor = (topicCategory: string) => {
    const topicColors: Record<string, string> = {
      business: 'text-blue-600 bg-blue-50 border-blue-200',
      personal: 'text-green-600 bg-green-50 border-green-200',
      technical: 'text-purple-600 bg-purple-50 border-purple-200',
      financial: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      health: 'text-red-600 bg-red-50 border-red-200',
      education: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    };
    return topicColors[topicCategory] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getEntityIcon = (entityType: string) => {
    const entityIcons: Record<string, string> = {
      person: '👤',
      place: '📍',
      organization: '🏢',
      date: '📅',
      event: '📋',
      product: '📦',
      concept: '💡',
    };
    return entityIcons[entityType] || '🔖';
  };

  const getTopicIcon = (topicCategory: string) => {
    const topicIcons: Record<string, string> = {
      business: '💼',
      personal: '👤',
      technical: '⚙️',
      financial: '💰',
      health: '🏥',
      education: '📚',
    };
    return topicIcons[topicCategory] || '📝';
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  const formatPercentage = (score: number) => {
    return `${Math.round(score * 100)}%`;
  };

  const parseKeywords = (keywordsJson: string): string[] => {
    try {
      return JSON.parse(keywordsJson);
    } catch {
      return [];
    }
  };

  if (entities.length === 0 && topics.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No entities or topics extracted yet
      </div>
    );
  }

  return (
    <div className="entity-topic-display space-y-4">
      {/* Entities Section */}
      {entities.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">
            🔍 Entities Detected ({entities.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {entities.slice(0, showDetails ? entities.length : 6).map((entity, index) => (
              <span
                key={index}
                className={`inline-flex items-center gap-1 rounded-full border font-medium ${getEntityColor(entity.entity_type)} ${sizeClasses[size]}`}
                title={`Type: ${entity.entity_type}\nConfidence: ${formatPercentage(entity.confidence)}${entity.context ? `\nContext: "${entity.context}"` : ''}`}
              >
                <span>{getEntityIcon(entity.entity_type)}</span>
                <span className="capitalize">{entity.entity_name}</span>
                <span className="text-xs opacity-75">
                  {formatPercentage(entity.confidence)}
                </span>
              </span>
            ))}
            {!showDetails && entities.length > 6 && (
              <span className="text-xs text-gray-500 px-2 py-1">
                +{entities.length - 6} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Topics Section */}
      {topics.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">
            📝 Topics Discussed ({topics.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {topics.slice(0, showDetails ? topics.length : 4).map((topic, index) => (
              <span
                key={index}
                className={`inline-flex items-center gap-1 rounded-full border font-medium ${getTopicColor(topic.topic_category)} ${sizeClasses[size]}`}
                title={`Category: ${topic.topic_category}\nRelevance: ${formatPercentage(topic.relevance_score)}\nConfidence: ${formatPercentage(topic.confidence)}${topic.summary ? `\nSummary: "${topic.summary}"` : ''}${topic.keywords ? `\nKeywords: ${parseKeywords(topic.keywords).join(', ')}` : ''}`}
              >
                <span>{getTopicIcon(topic.topic_category)}</span>
                <span>{topic.topic_name}</span>
                <span className="text-xs opacity-75">
                  {formatPercentage(topic.relevance_score)}
                </span>
              </span>
            ))}
            {!showDetails && topics.length > 4 && (
              <span className="text-xs text-gray-500 px-2 py-1">
                +{topics.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Relationships Section (only show in detailed view) */}
      {showDetails && relationships.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">
            🔗 Relationships ({relationships.length})
          </div>
          <div className="space-y-1">
            {relationships.map((relationship, index) => (
              <div
                key={index}
                className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1"
                title={`Strength: ${formatPercentage(relationship.relationship_strength)}\nConfidence: ${formatPercentage(relationship.confidence)}${relationship.context ? `\nContext: "${relationship.context}"` : ''}`}
              >
                <span className="font-medium">{relationship.entity1_name}</span>
                <span className="mx-1 text-gray-400">{relationship.relationship_type}</span>
                <span className="font-medium">{relationship.entity2_name}</span>
                <span className="ml-1 text-gray-400">
                  ({formatPercentage(relationship.relationship_strength)})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entity Type Breakdown (detailed view only) */}
      {showDetails && entities.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Entity Breakdown:</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(
              entities.reduce((acc, entity) => {
                acc[entity.entity_type] = (acc[entity.entity_type] || 0) + 1;
                return acc;
              }, {} as Record<string, number>)
            ).map(([type, count]) => (
              <div key={type} className="flex items-center gap-1">
                <span>{getEntityIcon(type)}</span>
                <span className="capitalize">{type}:</span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityTopicDisplay; 